import { useState } from 'react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Quick presets exposed in the popover sidebar. Each preset returns
 * the inclusive `[from, to]` range for that label, in the user's local
 * timezone (the popover writes ISO strings — the server interprets
 * them as instants regardless).
 *
 * "Custom" is a sentinel — clicking it just unlocks the calendar
 * without changing the current dates.
 */
export const DATE_PRESETS = [
  { label: 'Today', getValue: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
  {
    label: 'Last 7 days',
    getValue: () => ({ from: startOfDay(subDays(new Date(), 7)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Last 30 days',
    getValue: () => ({ from: startOfDay(subDays(new Date(), 30)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Last 90 days',
    getValue: () => ({ from: startOfDay(subDays(new Date(), 90)), to: endOfDay(new Date()) }),
  },
  { label: 'Custom', getValue: () => null },
] as const;

export type PresetLabel = (typeof DATE_PRESETS)[number]['label'];

interface Props {
  /** ISO start instant. */
  dateFrom?: string;
  /** ISO end instant. */
  dateTo?: string;
  onChange: (dateFrom: string, dateTo: string) => void;
  /** Currently-selected preset label, controlled by the parent for hint persistence. */
  preset?: PresetLabel;
  onPresetChange?: (label: PresetLabel) => void;
  className?: string;
}

/**
 * A range picker with quick presets. Mirrors the pattern from the CRM
 * `date-range-filter.tsx` — left rail of preset buttons, right side a
 * 2-month range calendar. Used in the dashboard header to scope
 * period-bounded metrics (completion_rate, completed_tasks,
 * referrals_count, the per-assignee completed counts).
 *
 * Uncontrolled mode: pass only `onChange` and the picker manages its
 * own preset state. Controlled mode: pass `preset` + `onPresetChange`
 * and the parent decides which preset label is highlighted.
 */
export function DashboardDateRangeFilter({
  dateFrom,
  dateTo,
  onChange,
  preset,
  onPresetChange,
  className,
}: Props): React.ReactElement {
  const [internalRange, setInternalRange] = useState<DateRange | undefined>(() => {
    if (dateFrom && dateTo) return { from: new Date(dateFrom), to: new Date(dateTo) };
    return {
      from: startOfDay(subDays(new Date(), 30)),
      to: endOfDay(new Date()),
    };
  });
  const [internalPreset, setInternalPreset] = useState<PresetLabel>(preset ?? 'Last 30 days');
  const [open, setOpen] = useState(false);

  // Keep the internal range synced if the parent updates `dateFrom`/`dateTo`.
  // Don't depend on `internalRange` to avoid loops.
  const externalRange =
    dateFrom && dateTo ? { from: new Date(dateFrom), to: new Date(dateTo) } : undefined;
  const range = externalRange ?? internalRange;
  const activePreset = preset ?? internalPreset;

  const setPreset = (next: PresetLabel): void => {
    if (onPresetChange) onPresetChange(next);
    else setInternalPreset(next);
  };

  const handleRangeChange = (next: DateRange | undefined): void => {
    setInternalRange(next);
    setPreset('Custom');
    if (next?.from && next?.to) {
      onChange(next.from.toISOString(), next.to.toISOString());
    }
  };

  const handlePresetClick = (entry: (typeof DATE_PRESETS)[number]): void => {
    setPreset(entry.label);
    if (entry.label === 'Custom') return;
    const next = entry.getValue();
    if (next) {
      setInternalRange(next);
      onChange(next.from.toISOString(), next.to.toISOString());
      setOpen(false);
    }
  };

  const formatRange = (): string => {
    if (!range?.from) return 'Select dates';
    if (!range.to) return format(range.from, 'MMM d, yyyy');
    if (activePreset !== 'Custom') return activePreset;
    return `${format(range.from, 'MMM d')} – ${format(range.to, 'MMM d, yyyy')}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('justify-start text-left font-normal min-w-[180px]', className)}
        >
          <CalendarIcon className="mr-1 h-4 w-4" />
          {formatRange()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex">
          <div className="border-r p-2 w-32">
            <div className="space-y-1">
              {DATE_PRESETS.map((entry) => (
                <Button
                  key={entry.label}
                  variant={activePreset === entry.label ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => handlePresetClick(entry)}
                >
                  {entry.label}
                </Button>
              ))}
            </div>
          </div>
          <Calendar
            mode="range"
            selected={range}
            onSelect={handleRangeChange}
            numberOfMonths={2}
            showOutsideDays={false}
            defaultMonth={range?.from}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
